import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  adminDocusealService,
  DocusealFieldCatalogEntry,
  DocusealSettings,
  DocusealTemplate,
  DocusealTemplateRow,
  DocusealVariablePreview,
} from '../services/docusealService';

type Feedback = { severity: 'success' | 'error' | 'info'; message: string } | null;

const AdminDocuseal: React.FC = () => {
  const [settings, setSettings] = useState<DocusealSettings | null>(null);
  const [rows, setRows] = useState<DocusealTemplateRow[]>([]);
  const [catalog, setCatalog] = useState<DocusealFieldCatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<Feedback>(null);

  // Settings form (secrets are write-only: blank means "leave unchanged")
  const [apiUrl, setApiUrl] = useState('');
  const [appUrl, setAppUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsData, templateRows, fieldCatalog] = await Promise.all([
        adminDocusealService.getSettings(),
        adminDocusealService.listTemplates(),
        adminDocusealService.fieldCatalog(),
      ]);
      setSettings(settingsData);
      setApiUrl(settingsData.api_url);
      setAppUrl(settingsData.app_url);
      setRows(templateRows);
      setCatalog(fieldCatalog);
    } catch (err: any) {
      setFeedback({
        severity: 'error',
        message: err?.response?.data?.message || 'Failed to load DocuSeal configuration.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveSettings = async (patch: Partial<DocusealSettings> & { api_key?: string; webhook_secret?: string }) => {
    setSavingSettings(true);
    try {
      const updated = await adminDocusealService.saveSettings({
        enabled: patch.enabled ?? settings?.enabled,
        api_url: patch.api_url ?? apiUrl,
        app_url: patch.app_url ?? appUrl,
        api_key: patch.api_key,
        webhook_secret: patch.webhook_secret,
      });
      setSettings(updated);
      setApiUrl(updated.api_url);
      setAppUrl(updated.app_url);
      setApiKey('');
      setWebhookSecret('');
      setFeedback({ severity: 'success', message: 'DocuSeal settings saved.' });
    } catch (err: any) {
      setFeedback({
        severity: 'error',
        message: err?.response?.data?.message || 'Failed to save settings.',
      });
    } finally {
      setSavingSettings(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const result = await adminDocusealService.testConnection();
      setFeedback({ severity: 'success', message: result.message });
    } catch (err: any) {
      setFeedback({
        severity: 'error',
        message: err?.response?.data?.message || 'Connection test failed.',
      });
    } finally {
      setTesting(false);
    }
  };

  const replaceTemplate = (agreementId: number, template: DocusealTemplate | null) => {
    setRows((prev) =>
      prev.map((row) => (row.agreement.id === agreementId ? { ...row, template } : row)),
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="DocuSeal"
          subheader="E-signature provider for agreements. When an agreement has no active DocuSeal template, the built-in HTML signing flow is used instead."
          action={
            <IconButton onClick={load} title="Refresh">
              <RefreshIcon />
            </IconButton>
          }
        />
        <Divider />
        <CardContent>
          <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
            <Chip
              label={settings?.is_configured ? 'API key configured' : 'API key missing'}
              color={settings?.is_configured ? 'success' : 'warning'}
              size="small"
            />
            <Chip
              label={settings?.is_enabled ? 'Enabled' : 'Disabled'}
              color={settings?.is_enabled ? 'success' : 'default'}
              size="small"
            />
            <Chip
              label={settings?.has_webhook_secret ? 'Webhook secret set' : 'No webhook secret'}
              color={settings?.has_webhook_secret ? 'success' : 'default'}
              size="small"
            />
          </Stack>

          {!settings?.is_enabled && (
            <Alert severity="info" sx={{ mb: 3 }}>
              DocuSeal is turned off. All agreements are currently signed with the HTML flow.
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
            }}
          >
            <TextField
              fullWidth
              label="API Base URL"
              value={apiUrl}
              onChange={(e) => setApiUrl(e.target.value)}
              helperText="https://api.docuseal.com for DocuSeal Cloud"
            />
            <TextField
              fullWidth
              label="Signing Form URL"
              value={appUrl}
              onChange={(e) => setAppUrl(e.target.value)}
              helperText="Host that serves signing forms (https://docuseal.com). Derived from the API URL — only change for self-hosted."
            />
            <TextField
              fullWidth
              type="password"
              label="API Key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={settings?.has_api_key ? '•••••••• (unchanged)' : 'Paste your DocuSeal API key'}
              helperText="Leave blank to keep the existing key"
            />
            <TextField
              fullWidth
              type="password"
              label="Webhook Secret"
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              placeholder={settings?.has_webhook_secret ? '•••••••• (unchanged)' : 'Optional shared secret'}
              helperText="Sent by DocuSeal as the X-Docuseal-Secret header"
            />
            <TextField
              fullWidth
              label="Webhook URL"
              value={settings?.webhook_url || ''}
              InputProps={{ readOnly: true }}
              helperText="Add this as a webhook endpoint in your DocuSeal account"
            />
          </Box>

          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 3 }} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              disabled={savingSettings}
              onClick={() =>
                saveSettings({
                  api_key: apiKey || undefined,
                  webhook_secret: webhookSecret || undefined,
                })
              }
            >
              {savingSettings ? 'Saving...' : 'Save Settings'}
            </Button>
            <Button variant="outlined" onClick={testConnection} disabled={testing || !settings?.is_configured}>
              {testing ? 'Testing...' : 'Test Connection'}
            </Button>
            <FormControlLabel
              control={
                <Switch
                  checked={!!settings?.enabled}
                  disabled={!settings?.is_configured}
                  onChange={(e) => saveSettings({ enabled: e.target.checked })}
                />
              }
              label="Enable DocuSeal signing"
            />
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardHeader
          title="Agreement Templates"
          subheader="Upload a DOCX per agreement. Placeholders written as [[TOKEN]] are detected automatically and mapped to PMP data."
        />
        <Divider />
        <CardContent>
          {rows.map((row) => (
            <TemplatePanel
              key={row.agreement.id}
              row={row}
              catalog={catalog}
              onChange={(template) => replaceTemplate(row.agreement.id, template)}
              onFeedback={setFeedback}
            />
          ))}
          {rows.length === 0 && <Typography color="text.secondary">No agreements found.</Typography>}
        </CardContent>
      </Card>

      <Snackbar
        open={!!feedback}
        autoHideDuration={6000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={feedback?.severity || 'info'} onClose={() => setFeedback(null)}>
          {feedback?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

interface TemplatePanelProps {
  row: DocusealTemplateRow;
  catalog: DocusealFieldCatalogEntry[];
  onChange: (template: DocusealTemplate | null) => void;
  onFeedback: (feedback: Feedback) => void;
}

const TemplatePanel: React.FC<TemplatePanelProps> = ({ row, catalog, onChange, onFeedback }) => {
  const { agreement, template } = row;
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState(template?.name || agreement.title);
  const [signerRole, setSignerRole] = useState(template?.signer_role || 'Client');
  const [variables, setVariables] = useState(template?.variables || []);
  const [preview, setPreview] = useState<DocusealVariablePreview[] | null>(null);

  useEffect(() => {
    setName(template?.name || agreement.title);
    setSignerRole(template?.signer_role || 'Client');
    setVariables(template?.variables || []);
  }, [template, agreement.title]);

  const statusChip = () => {
    if (!template) return <Chip size="small" label="HTML signing" />;
    if (!template.is_active) return <Chip size="small" label="Inactive" color="default" />;
    if (!template.is_ready_to_send) return <Chip size="small" label="Needs attention" color="warning" />;
    return <Chip size="small" label="DocuSeal active" color="success" />;
  };

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    try {
      await action();
    } catch (err: any) {
      onFeedback({
        severity: 'error',
        message: err?.response?.data?.message || 'Operation failed.',
      });
    } finally {
      setBusy(false);
    }
  };

  const saveTemplate = (patch: Record<string, unknown> = {}) =>
    run(async () => {
      const updated = await adminDocusealService.upsertTemplate({
        agreement_id: agreement.id,
        name,
        signer_role: signerRole,
        ...patch,
      });
      onChange(updated);
      onFeedback({ severity: 'success', message: 'Template saved.' });
    });

  const uploadDocx = (file: File) =>
    run(async () => {
      let current = template;
      // The upload endpoint needs a template row to attach the file to.
      if (!current) {
        current = await adminDocusealService.upsertTemplate({
          agreement_id: agreement.id,
          name,
          signer_role: signerRole,
        });
        onChange(current);
      }
      const result = await adminDocusealService.uploadDocx(current.id, file);
      onChange(result.data);
      onFeedback({ severity: 'success', message: result.message });
    });

  const updateVariable = (index: number, patch: Partial<typeof variables[number]>) => {
    setVariables((prev) => prev.map((v, i) => (i === index ? { ...v, ...patch } : v)));
  };

  const saveMappings = () =>
    run(async () => {
      if (!template) return;
      const updated = await adminDocusealService.saveVariableMappings(
        template.id,
        variables.map((v) => ({
          docx_variable: v.docx_variable,
          source: v.source,
          field_key: v.field_key,
          static_value: v.static_value,
        })),
      );
      onChange(updated);
      onFeedback({ severity: 'success', message: 'Field mapping saved.' });
    });

  const runPreview = () =>
    run(async () => {
      if (!template) return;
      const result = await adminDocusealService.previewVariables(template.id);
      setPreview(result.variables);
    });

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
          <Typography sx={{ flexGrow: 1 }}>
            {agreement.title}{' '}
            <Typography component="span" variant="body2" color="text.secondary">
              ({agreement.slug} · v{agreement.version})
            </Typography>
          </Typography>
          {template?.docx_filename && (
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
              {template.docx_filename}
            </Typography>
          )}
          {statusChip()}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {busy && <LinearProgressPlaceholder />}

        {template?.send_blockers?.length ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">This template will not be used yet:</Typography>
            <ul style={{ margin: '4px 0 0 18px' }}>
              {template.send_blockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
          </Alert>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1.5fr 2fr' },
            gap: 2,
            alignItems: 'start',
            mb: 2,
          }}
        >
          <TextField
            fullWidth
            size="small"
            label="Document name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="Shown to the signer in DocuSeal"
          />
          <TextField
            fullWidth
            size="small"
            label="Signer role"
            value={signerRole}
            onChange={(e) => setSignerRole(e.target.value)}
            helperText="Must match role= in the DOCX tags"
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="contained" size="small" disabled={busy} onClick={() => saveTemplate()}>
              Save
            </Button>
            <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />} disabled={busy}>
              {template?.has_docx ? 'Replace DOCX' : 'Upload DOCX'}
              <input
                type="file"
                accept=".docx"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadDocx(file);
                  e.target.value = '';
                }}
              />
            </Button>
          </Stack>
        </Box>

        {template?.has_docx && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Tooltip title="Re-scan the stored DOCX for placeholders">
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    const updated = await adminDocusealService.reparseDocx(template.id);
                    onChange(updated);
                    onFeedback({ severity: 'success', message: 'DOCX re-scanned.' });
                  })
                }
              >
                Re-scan
              </Button>
            </Tooltip>
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              disabled={busy}
              onClick={() =>
                adminDocusealService.downloadDocx(template.id, template.docx_filename || 'template.docx')
              }
            >
              Download
            </Button>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={busy}
              onClick={() =>
                run(async () => {
                  const updated = await adminDocusealService.removeDocx(template.id);
                  onChange(updated);
                  onFeedback({ severity: 'info', message: 'DOCX removed; reverted to HTML signing.' });
                })
              }
            >
              Remove DOCX
            </Button>
            <Chip
              size="small"
              label={
                template.has_signature_field
                  ? `${template.field_tags.length} signature field(s) in DOCX`
                  : 'No signature field in DOCX'
              }
              color={template.has_signature_field ? 'success' : 'warning'}
            />
          </Stack>
        )}

        {template && (
          <Stack direction="row" spacing={3} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <FormControlLabel
              control={
                <Switch
                  checked={template.is_active}
                  disabled={busy}
                  onChange={(e) => saveTemplate({ is_active: e.target.checked })}
                />
              }
              label="Use DocuSeal for this agreement"
            />
            <Tooltip title="Adds a signature, name, designation and date block to the end of the document at send time. Use only until the DOCX itself contains signature tags.">
              <FormControlLabel
                control={
                  <Switch
                    checked={template.append_signature_block}
                    disabled={busy}
                    onChange={(e) => saveTemplate({ append_signature_block: e.target.checked })}
                  />
                }
                label="Append signature block automatically"
              />
            </Tooltip>
          </Stack>
        )}

        {template?.has_docx && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                Detected placeholders ({variables.length})
              </Typography>
              <Button size="small" startIcon={<VisibilityIcon />} onClick={runPreview} disabled={busy}>
                Preview values
              </Button>
            </Stack>

            {variables.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No [[TOKEN]] placeholders found in this document. It will be sent as-is for signature.
              </Typography>
            ) : (
              <>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Placeholder</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Mapped to</TableCell>
                      <TableCell>Preview</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {variables.map((variable, index) => {
                      const previewRow = preview?.find((p) => p.docx_variable === variable.docx_variable);
                      return (
                        <TableRow key={variable.docx_variable}>
                          <TableCell>
                            <code>[[{variable.docx_variable}]]</code>
                          </TableCell>
                          <TableCell sx={{ minWidth: 130 }}>
                            <TextField
                              select
                              size="small"
                              fullWidth
                              value={variable.source}
                              onChange={(e) =>
                                updateVariable(index, { source: e.target.value as 'field' | 'static' })
                              }
                            >
                              <MenuItem value="field">PMP field</MenuItem>
                              <MenuItem value="static">Fixed text</MenuItem>
                            </TextField>
                          </TableCell>
                          <TableCell sx={{ minWidth: 240 }}>
                            {variable.source === 'static' ? (
                              <TextField
                                size="small"
                                fullWidth
                                value={variable.static_value || ''}
                                onChange={(e) => updateVariable(index, { static_value: e.target.value })}
                                placeholder="Text to insert"
                              />
                            ) : (
                              <TextField
                                select
                                size="small"
                                fullWidth
                                value={variable.field_key || ''}
                                onChange={(e) => updateVariable(index, { field_key: e.target.value })}
                              >
                                <MenuItem value="">
                                  <em>Not mapped</em>
                                </MenuItem>
                                {catalog.map((entry) => (
                                  <MenuItem key={entry.key} value={entry.key}>
                                    {entry.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>
                            {previewRow ? (
                              <Typography
                                variant="body2"
                                color={previewRow.is_resolved ? 'text.primary' : 'warning.main'}
                                sx={{ wordBreak: 'break-word' }}
                              >
                                {previewRow.is_resolved ? previewRow.resolved_value : 'empty'}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                <Box sx={{ mt: 2 }}>
                  <Button variant="contained" size="small" onClick={saveMappings} disabled={busy}>
                    Save Mapping
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        )}

        {!template && (
          <Alert severity="info">
            No DocuSeal template configured — this agreement uses the HTML signing flow. Upload a DOCX to switch it to
            DocuSeal.
          </Alert>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const LinearProgressPlaceholder: React.FC = () => (
  <Box sx={{ mb: 2 }}>
    <CircularProgress size={18} />
  </Box>
);

export default AdminDocuseal;
